import useConfirm from "../../components/modal/useConfirm";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import Button from "../../components/button/Button";
import {
  fetchAllCarousel,
  modifyCarousalImageStatus,
  resetCarouselForm,
  setIsEditMode,
  fetchScrollInfo,
  saveScrollInfo,
} from "../../features/carouselSlice";
import Spinner from "../../components/spinner/Spinner";
import { useEffect, useMemo, useState } from "react";
import { BasicModal } from "../../components/modal/ModalUI";
import CarouselModal from "./CarouselModal";
import { basicModalShowHide, setTitle } from "../../features/modalSlice";
import { can } from "../../utils/permission.util";
import {
  RowActionButton,
  RowActionIcons,
  RowActions,
} from "../../components/data-table/RowActions";

const safeCarouselUrl = (value) => {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.href : null;
  } catch (_) {
    return null;
  }
};

const Carousel = () => {
  const [askConfirm, confirmDialog] = useConfirm();
  const dispatch = useDispatch();
  const {
    listData,
    isLoad,
    isLoadStatus,
    scrollInfo,
    isScrollLoad,
    isScrollSaving,
  } = useSelector((state) => state.carousel);

  const [statusFilter, setStatusFilter] = useState("all");

  // Local UI state for checkboxes, reflecting Redux scrollInfo
  const [huntChecked, setHuntChecked] = useState(false);
  const [questChecked, setQuestChecked] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");

  // Load list + scroll settings on mount
  useEffect(() => {
    dispatch(fetchAllCarousel());
    dispatch(fetchScrollInfo());
  }, [dispatch]);

  // Sync local checkboxes when scrollInfo updates
  useEffect(() => {
    if (scrollInfo) {
      setHuntChecked(Number(scrollInfo.isHunt) === 1);
      setQuestChecked(Number(scrollInfo.isQuest) === 1);
    }
  }, [scrollInfo]);

  function addCarouselImage() {
    dispatch(resetCarouselForm());
    dispatch(basicModalShowHide());
    dispatch(setTitle("Add Carousel Item"));
  }

  const updateStatus = (id, status) => {
    const run = () => dispatch(modifyCarousalImageStatus({ id, status }));
    if (status === 2) askConfirm("Delete this slide? This can't be undone here.", run, { label: "Delete" });
    else if (status === 0) askConfirm("Deactivate this slide? It will stop showing in the app.", run, { label: "Deactivate", danger: false });
    else run();
  };
  const deleteImg = updateStatus;

  function editPackage(item) {
    dispatch(
      setIsEditMode({
        isEdit: true,
        editId: item.id,
        selectedType: item.type,
        carouselImage: item.img_url,
        carouselLink_Url: item.link_url || "",
        sequence: item.sequence,
        timing: item.timing ?? 0, // make sure current timing is loaded
      }),
    );
    dispatch(basicModalShowHide());
  }

  // Handlers for the two checkboxes (same line as dropdown)
  const onToggleHunt = () => {
    const nextHunt = !huntChecked;
    setHuntChecked(nextHunt);

    const payload = {
      id: 1,
      isHunt: nextHunt ? 1 : 0,
      isQuest: questChecked ? 1 : 0,
    };

    // Only call API if something actually changed vs Redux
    const changed =
      Number(scrollInfo.isHunt) !== payload.isHunt ||
      Number(scrollInfo.isQuest) !== payload.isQuest;
    if (changed) dispatch(saveScrollInfo(payload));
  };

  const onToggleQuest = () => {
    const nextQuest = !questChecked;
    setQuestChecked(nextQuest);

    const payload = {
      id: 1,
      isHunt: huntChecked ? 1 : 0,
      isQuest: nextQuest ? 1 : 0,
    };

    const changed =
      Number(scrollInfo.isHunt) !== payload.isHunt ||
      Number(scrollInfo.isQuest) !== payload.isQuest;
    if (changed) dispatch(saveScrollInfo(payload));
  };

  const typeOptions = useMemo(() => {
    if (!listData || !listData.length) return [];
    const uniq = new Set();
    listData.forEach((item) => {
      const t = (item?.type_label || "").trim();
      if (t) uniq.add(t);
    });
    return Array.from(uniq).sort();
  }, [listData]);

  const filteredList = useMemo(() => {
    if (!listData || !listData.length) return [];

    return listData.filter((item) => {
      // status filter
      const statusOk =
        statusFilter === "all" ? true : String(item.status) === statusFilter;

      // type filter
      const itemType = (item?.type_label || "").trim();
      const typeOk = typeFilter === "all" ? true : itemType === typeFilter;

      return statusOk && typeOk;
    });
  }, [listData, statusFilter, typeFilter]);

  //   const filteredList = useMemo(() => {
  //     if (!listData || !listData.length) return [];
  //     return listData.filter((item) => {
  //       if (statusFilter === "all") return true;
  //       return String(item.status) === statusFilter;
  //     });
  //   }, [listData, statusFilter]);

  return (
    <div id="main" className="main">
      <Breadcrumb page="Carousel" />
      <section className="section">
        <div className="row mb-3">
          <div className="col-sm-6 col-md-12 col-lg-12 d-flex justify-content-between align-items-center">
            {/* Left section: Title + Filters */}
            <div className="d-flex align-items-center gap-3">
              <h6 className="card-title mb-0">Carousel List</h6>

              {/* Status Filter */}
              <select
                className="form-select form-select-sm"
                style={{ width: "150px" }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>

              {/* new filter */}
              {/* Type Filter */}
              <select
                className="form-select form-select-sm"
                style={{ width: "180px" }}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                {typeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {/* Hunt Scroll */}
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="huntScrollCheckbox"
                  checked={huntChecked}
                  onChange={onToggleHunt}
                  disabled={isScrollLoad || isScrollSaving}
                />
                <label
                  className="form-check-label"
                  htmlFor="huntScrollCheckbox"
                >
                  Hunt Autoplay
                </label>
              </div>

              {/* Quest Scroll */}
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="questScrollCheckbox"
                  checked={questChecked}
                  onChange={onToggleQuest}
                  disabled={isScrollLoad || isScrollSaving}
                />
                <label
                  className="form-check-label"
                  htmlFor="questScrollCheckbox"
                >
                  Quest Autoplay
                </label>
              </div>

              {/* Saving indicator */}
              {(isScrollLoad || isScrollSaving) && (
                <span className="text-muted small">Saving…</span>
              )}
            </div>

            {/* Right: Add Button */}
            {can("/carousel", "create") && (
              <Button
                className="btn btn-sm btn-outline-primary me-2"
                title="Create Discount"
                onClick={addCarouselImage}
              >
                <i className="fa-solid fa-plus" />
              </Button>
            )}
          </div>

          {/* Table */}
          {isLoad ? (
            <Spinner />
          ) : (
            <table className="table table-bordered mt-3">
              <thead>
                <tr>
                  <th scope="col" className="text-center fs-7">
                    Type
                  </th>
                  <th scope="col" className="text-center fs-7">
                    Carousel Link
                  </th>
                  <th scope="col" className="text-center fs-7">
                    Sequence
                  </th>
                  <th scope="col" className="text-center fs-7">
                    Carousel Image (3:2)
                  </th>
{(can("/carousel", "create") || can("/carousel", "delete")) && (

                  <th scope="col" className="text-center fs-7" width="15%">
                    Action
                  </th>
                    )}
                </tr>
              </thead>
              <tbody>
                {filteredList.length ? (
                  filteredList.map((list, i) => (
                    <tr key={`carousel_listData-${i}`}>
                      <td className="text-center fs-7 align-middle">
                        {list?.type_label || null}
                      </td>
                      <td className="text-center fs-7 align-middle">
                        {safeCarouselUrl(list?.link_url) ? (
                          <a
                            href={safeCarouselUrl(list.link_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {list.link_url}
                          </a>
                        ) : null}
                      </td>
                      <td className="text-center fs-7 align-middle">
                        {list?.sequence}
                      </td>
                      <td className="text-center fs-7">
                        {list?.img_url ? (
                          <img
                            src={list?.img_url || null}
                            alt="banner"
                            height="64"
                            width="96"
                            style={{ objectFit: "cover" }}
                          />
                        ) : null}
                      </td>
{(can("/carousel", "create") || can("/carousel", "delete")) && (

                      <td className="text-center fs-7 align-middle">
                        <RowActions>
                          {list?.status === 1
                            ? can("/carousel", "create") && (
                                <RowActionButton
                                  label="Unpublish"
                                  iconClass={RowActionIcons.unpublish}
                                  variant="outline-success"
                                  disabled={isLoadStatus}
                                  onClick={() => updateStatus(list.id, 0)}
                                />
                              )
                            : can("/carousel", "create") && (
                                <RowActionButton
                                  label="Publish to store"
                                  iconClass={RowActionIcons.publish}
                                  variant="outline-danger"
                                  disabled={isLoadStatus}
                                  onClick={() => updateStatus(list.id, 1)}
                                />
                              )}
                          {can("/carousel", "create") && (
                            <RowActionButton
                              label="Edit"
                              iconClass={RowActionIcons.edit}
                              variant="outline-success"
                              onClick={() => editPackage(list || null)}
                            />
                          )}
                          {can("/carousel", "delete") && (
                            <RowActionButton
                              label="Delete"
                              iconClass={RowActionIcons.delete}
                              variant="outline-danger"
                              disabled={isLoadStatus}
                              onClick={() => deleteImg(list.id, 2)}
                            />
                          )}
                        </RowActions>
                      </td>
            )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="text-center fs-7" colSpan={4}>
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <BasicModal>
        <CarouselModal />
      </BasicModal>
      {confirmDialog}
    </div>
  );
};

export default Carousel;
